import { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

// 48 for button, 16 for bottom padding
const PROFILE_BUTTON_HEIGHT = 48 + 16;
const MORE_BUTTON_HEIGHT = 54;
export function labelToId(label: string): string {
  return `${label.toLowerCase().replace(' ', '-')}-content`;
}

export function idToLabel(id: string): string {
  return id.replace('-content', '').replace(/-/g, ' ');
}

export function useOverflowItems(
  expanded: boolean,
  elementsLoading: boolean,
  expectedItems: number,
  scrollContainerRef?: RefObject<HTMLDivElement>
): { overflowItems: string[]; hasMeasured: boolean } {
  const itemIds = useMemo(
    () => [
      'home-content',
      'observability-content',
      'evaluation-content',
      'prompt-engineering-content',
      'langgraph-platform-content',
      'settings',
      'secondary-content',
    ],
    []
  );

  const measurementsRef = useRef<{
    expanded: Record<string, number>;
    collapsed: Record<string, number>;
  }>({
    expanded: {},
    collapsed: {},
  });

  const [hasMeasured, setHasMeasured] = useState<{
    expanded: boolean;
    collapsed: boolean;
  }>({
    expanded: false,
    collapsed: false,
  });

  const [overflowItems, setOverflowItems] = useState<string[]>([]);

  const debouncedSetOverflowItems = useDebouncedCallback(setOverflowItems, 100);

  const applicableHasMeasured =
    hasMeasured[expanded ? 'expanded' : 'collapsed'];

  // We're trying to get the measurement of the items the first time they render.
  // Some of the logic in here is to make sure it works for both expanded and collapsed states.
  // The approach is to display none on items that are hidden instead of removing them from the DOM.
  // Then when we measure, we temporarily display them and store the measurements.
  useEffect(() => {
    if (
      elementsLoading ||
      !scrollContainerRef?.current ||
      hasMeasured[expanded ? 'expanded' : 'collapsed']
    )
      return;
    // Temporarily show all items and store original display values
    const originalStyles: { element: HTMLElement; display: string }[] = [];
    itemIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        originalStyles.push({
          element,
          display: element.style.display,
        });
        element.style.cssText = `
          display: flex !important;
        `;
      }
    });

    // Measure items
    const newMeasurements: Record<string, number> = {};
    let prevExistingElementId = itemIds[0];
    itemIds.forEach((id) => {
      const element = document.getElementById(id);
      if (element) {
        const rect = element.getBoundingClientRect();
        if (id === 'secondary-content') {
          const prevElement = document.getElementById(prevExistingElementId);

          newMeasurements[id] =
            (prevElement?.getBoundingClientRect().bottom ?? 50) +
            100 +
            rect.height; // keep an 100px buffer between secondary links and main nav
        } else {
          newMeasurements[id] = rect.bottom;
        }
        prevExistingElementId = id;
      }
    });

    // Restore original styles
    originalStyles.forEach(({ element, display }) => {
      element.style.display = display;
    });

    measurementsRef.current = {
      ...measurementsRef.current,
      [expanded ? 'expanded' : 'collapsed']: newMeasurements,
    };

    setHasMeasured({
      ...hasMeasured,
      [expanded ? 'expanded' : 'collapsed']:
        Object.keys(newMeasurements).length === expectedItems,
    });
  }, [
    expanded,
    elementsLoading,
    itemIds,
    scrollContainerRef,
    hasMeasured,
    expectedItems,
  ]);

  useEffect(() => {
    if (
      !scrollContainerRef ||
      !scrollContainerRef.current ||
      !applicableHasMeasured
    )
      return;

    const updateOverflowItems = () => {
      const containerHeight = scrollContainerRef.current?.clientHeight ?? 0;
      const availableHeight = containerHeight - PROFILE_BUTTON_HEIGHT;
      const newOverflowItems: string[] = [];

      itemIds.forEach((id) => {
        // if we're measure secondary content, we don't
        // want to include the height of the more button since
        // it doesn't exists yet and will replace the secondary content.
        const moreButtonOffset =
          id !== 'secondary-content' ? MORE_BUTTON_HEIGHT : 0;

        if (
          measurementsRef.current[expanded ? 'expanded' : 'collapsed'][id] >
          availableHeight - moreButtonOffset
        ) {
          newOverflowItems.push(id);
        }
      });

      debouncedSetOverflowItems(newOverflowItems);
    };

    const resizeObserver = new ResizeObserver(updateOverflowItems);
    resizeObserver.observe(scrollContainerRef.current);

    updateOverflowItems();

    return () => {
      resizeObserver.disconnect();
    };
  }, [
    scrollContainerRef,
    itemIds,
    expanded,
    debouncedSetOverflowItems,
    applicableHasMeasured,
  ]);

  return {
    overflowItems,
    hasMeasured: applicableHasMeasured,
  };
}
